import React, { useState, useEffect, useCallback } from 'react';
import { workflowsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Plus, Loader2, Trash2, ArrowLeft, Workflow, Calendar, CheckSquare, GripVertical, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = [
  { value: '', label: 'None' },
  { value: 'general', label: 'General' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'permit', label: 'Permit' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'material_order', label: 'Material Order' },
  { value: 'closeout', label: 'Closeout' },
];

const ANCHORS = [
  { value: 'phase_start', label: 'Phase Start' },
  { value: 'phase_end', label: 'Phase End' },
  { value: 'job_start', label: 'Job Start' },
  { value: 'job_end', label: 'Job End' },
];

const ROLES = [
  { value: '', label: 'Unassigned' },
  { value: 'owner', label: 'Owner' },
  { value: 'office', label: 'Office' },
  { value: 'field', label: 'Field Tech' },
];

const DEFAULT_COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#06B6D4','#84CC16'];

export default function WorkflowSettings() {
  const navigate = useNavigate();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editWf, setEditWf] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({ name: '', description: '', job_mode: 'project' });
  const [phases, setPhases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [expandedSection, setExpandedSection] = useState('phases');

  const fetchWorkflows = useCallback(async () => {
    try {
      const res = await workflowsAPI.list();
      setWorkflows(res.data.workflows || []);
    } catch { toast.error('Failed to load workflows'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  const openCreate = () => {
    setEditWf(null);
    setForm({ name: '', description: '', job_mode: 'project' });
    setPhases([]);
    setTasks([]);
    setExpandedSection('phases');
    setDrawerOpen(true);
  };

  const openEdit = async (wf) => {
    try {
      const res = await workflowsAPI.get(wf.id);
      const data = res.data;
      setEditWf(data);
      setForm({ name: data.name, description: data.description || '', job_mode: data.job_mode });
      setPhases(data.schedule_phases.map(p => ({
        name: p.name, description: p.description || '', color: p.color || '',
        offset_from_start_days: String(p.offset_from_start_days || 0),
        duration_days: String(p.duration_days || 7),
      })));
      setTasks(data.tasks.map(t => ({
        title: t.title, description: t.description || '', category: t.category || '',
        priority: t.priority, due_date_mode: t.due_date_mode,
        date_offset_anchor: t.date_offset_anchor || '', date_offset_days: String(t.date_offset_days || 0),
        schedule_phase_index: t.schedule_phase_index != null ? String(t.schedule_phase_index) : '',
        assigned_role: t.assigned_role || '',
      })));
      setExpandedSection('phases');
      setDrawerOpen(true);
    } catch { toast.error('Failed to load workflow'); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Workflow name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || undefined,
        job_mode: form.job_mode,
        schedule_phases: phases.map((p, i) => ({
          name: p.name.trim(), description: p.description || undefined,
          color: p.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
          offset_from_start_days: parseInt(p.offset_from_start_days) || 0,
          duration_days: parseInt(p.duration_days) || 7, sort_order: i,
        })),
        tasks: tasks.map((t, i) => ({
          title: t.title.trim(), description: t.description || undefined,
          category: t.category || undefined, priority: t.priority || 'normal',
          due_date_mode: t.due_date_mode || 'auto',
          date_offset_anchor: t.date_offset_anchor || undefined,
          date_offset_days: parseInt(t.date_offset_days) || 0,
          schedule_phase_index: t.schedule_phase_index !== '' ? parseInt(t.schedule_phase_index) : undefined,
          assigned_role: t.assigned_role || undefined, sort_order: i,
        })),
      };
      if (editWf) {
        await workflowsAPI.update(editWf.id, payload);
        toast.success('Workflow updated');
      } else {
        await workflowsAPI.create(payload);
        toast.success('Workflow created');
      }
      setDrawerOpen(false);
      fetchWorkflows();
    } catch { toast.error('Failed to save workflow'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workflow? Existing jobs that used it will not be affected.')) return;
    try {
      await workflowsAPI.delete(id);
      toast.success('Workflow deleted');
      if (editWf?.id === id) setDrawerOpen(false);
      fetchWorkflows();
    } catch { toast.error('Failed to delete workflow'); }
  };

  const handleDuplicate = async (wf) => {
    const name = window.prompt('Name for the copy:', `Copy of ${wf.name}`);
    if (name === null) return; // cancelled
    try {
      const res = await workflowsAPI.duplicate(wf.id, { name: name || undefined });
      toast.success(`Duplicated as "${res.data.name}"`);
      fetchWorkflows();
    } catch { toast.error('Failed to duplicate workflow'); }
  };

  // Phase helpers
  const addPhase = () => setPhases(prev => [...prev, { name: '', description: '', color: DEFAULT_COLORS[prev.length % DEFAULT_COLORS.length], offset_from_start_days: String(prev.length > 0 ? parseInt(prev[prev.length-1].offset_from_start_days || 0) + parseInt(prev[prev.length-1].duration_days || 7) : 0), duration_days: '7' }]);
  const removePhase = (i) => setPhases(prev => prev.filter((_, idx) => idx !== i));
  const updatePhase = (i, field, val) => setPhases(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: val } : p));

  // Task helpers
  const addTask = () => setTasks(prev => [...prev, { title: '', description: '', category: '', priority: 'normal', due_date_mode: 'auto', date_offset_anchor: 'phase_start', date_offset_days: '0', schedule_phase_index: phases.length > 0 ? '0' : '', assigned_role: '' }]);
  const removeTask = (i) => setTasks(prev => prev.filter((_, idx) => idx !== i));
  const updateTask = (i, field, val) => setTasks(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: val } : t));

  const modeLabel = (m) => m === 'project' ? 'Project' : m === 'service' ? 'Service' : 'Both';

  if (loading) {
    return <Layout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="workflow-settings">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 transition-colors" data-testid="wf-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Workflows</h1>
              <p className="text-sm text-muted-foreground mt-1">Create reusable workflows with schedule phases and task templates. Apply them to jobs to auto-generate work.</p>
            </div>
            {canEdit && (
              <Button onClick={openCreate} data-testid="new-workflow-btn">
                <Plus className="w-4 h-4 mr-2" /> New Workflow
              </Button>
            )}
          </div>
        </div>

        {workflows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="workflows-empty">
            <Workflow className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No workflows yet</p>
            <p className="text-sm mt-1">Create your first workflow to automate task and schedule generation for jobs.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="workflow-list">
            {workflows.map(wf => (
              <Card key={wf.id} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => openEdit(wf)} data-testid={`workflow-card-${wf.id}`}>
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-sm truncate">{wf.name}</CardTitle>
                      {wf.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{wf.description}</p>}
                    </div>
                    <Badge variant="outline" className="text-[10px] ml-2 flex-shrink-0">{modeLabel(wf.job_mode)}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{wf.schedule_phase_count} phase{wf.schedule_phase_count !== 1 ? 's' : ''}</span>
                      <span className="flex items-center gap-1"><CheckSquare className="w-3 h-3" />{wf.task_count} task{wf.task_count !== 1 ? 's' : ''}</span>
                    </div>
                    {canEdit && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); handleDuplicate(wf); }} title="Duplicate workflow" data-testid={`duplicate-workflow-${wf.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  {!wf.is_active && <Badge variant="secondary" className="text-[10px] mt-2">Inactive</Badge>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Workflow Editor Drawer */}
        <AppDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title={editWf ? 'Edit Workflow' : 'New Workflow'} description="Define the schedule phases and tasks for this workflow" size="lg">
          <div className="space-y-5 p-1">
            {/* Basic Info */}
            <div className="space-y-3">
              <div>
                <Label>Workflow Name *</Label>
                <Input autoFocus value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. HVAC Install, Roofing Project..." data-testid="wf-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Job Type</Label>
                  <Select value={form.job_mode} onValueChange={v => setForm(f => ({...f, job_mode: v}))}>
                    <SelectTrigger data-testid="wf-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project">Project Jobs</SelectItem>
                      <SelectItem value="service">Service Jobs</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Optional" />
                </div>
              </div>
            </div>

            {/* Schedule Phases Section */}
            <div className="border rounded-lg overflow-hidden">
              <button className="w-full flex items-center justify-between px-3 py-2.5 bg-muted/30 text-sm font-semibold hover:bg-muted/40 transition-colors" onClick={() => setExpandedSection(expandedSection === 'phases' ? '' : 'phases')} data-testid="wf-phases-header">
                <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Schedule Phases ({phases.length})</span>
                {expandedSection === 'phases' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {expandedSection === 'phases' && (
                <div className="p-3 space-y-3">
                  {phases.map((p, i) => (
                    <div key={i} className="flex gap-2 items-start p-2.5 border rounded-lg bg-background" data-testid={`wf-phase-${i}`}>
                      <div className="w-3 h-3 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: p.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length] }} />
                      <div className="flex-1 space-y-2">
                        <Input value={p.name} onChange={e => updatePhase(i, 'name', e.target.value)} placeholder="Phase name" className="h-8 text-sm" />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Starts (days from job start)</Label>
                            <Input type="number" value={p.offset_from_start_days} onChange={e => updatePhase(i, 'offset_from_start_days', e.target.value)} className="h-7 text-xs" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Duration (days)</Label>
                            <Input type="number" value={p.duration_days} onChange={e => updatePhase(i, 'duration_days', e.target.value)} className="h-7 text-xs" />
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => removePhase(i)} data-testid={`wf-remove-phase-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="w-full" onClick={addPhase} data-testid="wf-add-phase">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Phase
                  </Button>
                </div>
              )}
            </div>

            {/* Tasks Section */}
            <div className="border rounded-lg overflow-hidden">
              <button className="w-full flex items-center justify-between px-3 py-2.5 bg-muted/30 text-sm font-semibold hover:bg-muted/40 transition-colors" onClick={() => setExpandedSection(expandedSection === 'tasks' ? '' : 'tasks')} data-testid="wf-tasks-header">
                <span className="flex items-center gap-2"><CheckSquare className="w-4 h-4" /> Tasks ({tasks.length})</span>
                {expandedSection === 'tasks' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {expandedSection === 'tasks' && (
                <div className="p-3 space-y-3">
                  {tasks.map((t, i) => (
                    <div key={i} className="p-2.5 border rounded-lg bg-background space-y-2" data-testid={`wf-task-${i}`}>
                      <div className="flex gap-2 items-start">
                        <div className="flex-1">
                          <Input value={t.title} onChange={e => updateTask(i, 'title', e.target.value)} placeholder="Task title" className="h-8 text-sm" />
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => removeTask(i)} data-testid={`wf-remove-task-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Category</Label>
                          <Select value={t.category || 'none'} onValueChange={v => updateTask(i, 'category', v === 'none' ? '' : v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value || 'none'} value={c.value || 'none'}>{c.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Priority</Label>
                          <Select value={t.priority} onValueChange={v => updateTask(i, 'priority', v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Assign To</Label>
                          <Select value={t.assigned_role || 'none'} onValueChange={v => updateTask(i, 'assigned_role', v === 'none' ? '' : v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{ROLES.map(r => <SelectItem key={r.value || 'none'} value={r.value || 'none'}>{r.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      {/* Due date config */}
                      <div className="grid grid-cols-3 gap-2">
                        {phases.length > 0 && (
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Linked Phase</Label>
                            <Select value={t.schedule_phase_index !== '' ? t.schedule_phase_index : 'none'} onValueChange={v => updateTask(i, 'schedule_phase_index', v === 'none' ? '' : v)}>
                              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {phases.map((p, pi) => <SelectItem key={pi} value={String(pi)}>{p.name || `Phase ${pi + 1}`}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Due From</Label>
                          <Select value={t.date_offset_anchor || 'none'} onValueChange={v => updateTask(i, 'date_offset_anchor', v === 'none' ? '' : v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Manual</SelectItem>
                              {ANCHORS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Offset (days)</Label>
                          <Input type="number" value={t.date_offset_days} onChange={e => updateTask(i, 'date_offset_days', e.target.value)} className="h-7 text-xs" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="w-full" onClick={addTask} data-testid="wf-add-task">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Task
                  </Button>
                </div>
              )}
            </div>

            {/* Save / Duplicate / Delete */}
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="wf-save-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editWf ? 'Update Workflow' : 'Create Workflow'}
              </Button>
              {editWf && canEdit && (
                <Button variant="outline" size="icon" onClick={() => handleDuplicate(editWf)} title="Duplicate" data-testid="wf-duplicate-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              )}
              {editWf && canEdit && (
                <Button variant="destructive" size="icon" onClick={() => handleDelete(editWf.id)} data-testid="wf-delete-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </AppDrawer>
      </div>
    </Layout>
  );
}
